import { useState, useEffect, useMemo } from 'react';
import { inventorySessionsApi, InventorySessionsData, InventorySession } from '../../api/inventorySessionsApi';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';

export interface DailyBreakdown {
    date: string;
    minutes: number;
    sessionCount: number;
}

export interface BuildingSessionSummary {
    buildingKey: string;
    buildingName: string;
    totalMinutes: number;
    sessionCount: number;
    dailyBreakdown: DailyBreakdown[];
}

/**
 * Parse session duration in minutes from start and end times
 * Times are formatted as "YYYY-MM-DD hh:mm:ss"
 */
function calculateDurationMinutes(startTime: string, endTime: string | null): number {
    if (!endTime) return 0; // Incomplete session

    const start = new Date(startTime.replace(' ', 'T'));
    const end = new Date(endTime.replace(' ', 'T'));

    const diffMs = end.getTime() - start.getTime();
    return Math.max(0, Math.round(diffMs / 60000)); // Convert to minutes, floor at 0
}

/**
 * Extract date (YYYY-MM-DD) from timestamp
 */
function extractDate(timestamp: string): string {
    return timestamp.split(' ')[0];
}

/**
 * Check if a date string is within the given range (inclusive)
 */
function isDateInRange(dateStr: string, startDate: string, endDate: string): boolean {
    return dateStr >= startDate && dateStr <= endDate;
}

export function useInventorySessions(startDate: string, endDate: string) {
    const [rawData, setRawData] = useState<InventorySessionsData>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const { buildings } = useBuildingsData();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await inventorySessionsApi.getAllSessions();
                setRawData(data);
            } catch (err) {
                console.error('Error fetching inventory sessions:', err);
                setError('Kunde inte ladda inventeringstider');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const summaries = useMemo((): BuildingSessionSummary[] => {
        const buildingMap = new Map<string, {
            sessions: { session: InventorySession; date: string; minutes: number }[];
        }>();

        // Process all sessions
        Object.entries(rawData).forEach(([buildingKey, sessions]) => {
            Object.values(sessions).forEach((session) => {
                const sessionDate = extractDate(session.startTime);

                // Filter by date range
                if (!isDateInRange(sessionDate, startDate, endDate)) {
                    return;
                }

                const minutes = calculateDurationMinutes(session.startTime, session.endTime);

                if (!buildingMap.has(buildingKey)) {
                    buildingMap.set(buildingKey, { sessions: [] });
                }

                buildingMap.get(buildingKey)!.sessions.push({
                    session,
                    date: sessionDate,
                    minutes,
                });
            });
        });

        // Convert to summaries
        const result: BuildingSessionSummary[] = [];

        buildingMap.forEach((data, buildingKey) => {
            // Look up building name
            const building = buildings?.find((b) => b.key === buildingKey);
            const buildingName = building?.name || buildingKey;

            // Group by date
            const dailyMap = new Map<string, { minutes: number; count: number }>();
            let totalMinutes = 0;

            data.sessions.forEach(({ date, minutes }) => {
                totalMinutes += minutes;

                if (!dailyMap.has(date)) {
                    dailyMap.set(date, { minutes: 0, count: 0 });
                }
                const daily = dailyMap.get(date)!;
                daily.minutes += minutes;
                daily.count += 1;
            });

            // Convert daily map to sorted array (newest first)
            const dailyBreakdown: DailyBreakdown[] = Array.from(dailyMap.entries())
                .map(([date, { minutes, count }]) => ({
                    date,
                    minutes,
                    sessionCount: count,
                }))
                .sort((a, b) => b.date.localeCompare(a.date));

            result.push({
                buildingKey,
                buildingName,
                totalMinutes,
                sessionCount: data.sessions.length,
                dailyBreakdown,
            });
        });

        // Sort by total time descending
        return result.sort((a, b) => b.totalMinutes - a.totalMinutes);
    }, [rawData, startDate, endDate, buildings]);

    return { summaries, loading, error };
}
